import {
  AfterViewInit,
  ChangeDetectorRef,
  Component, ElementRef, EventEmitter,
  Input,
  OnChanges,
  OnInit, Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import {TimePickerComponent} from '@niceltd/sol/time-picker';
import {TextInputComponent} from '@niceltd/sol/text-input';
import {DateUtils} from '../../../../common/utils/date';

const now = new Date();

@Component({
  standalone: false,
  selector: 'esp-operating-hours',
  templateUrl: './operating-hours.component.html',
  styleUrls: ['./operating-hours.component.css']
})
export class OperatingHoursComponent implements OnInit,OnChanges, AfterViewInit{
  @ViewChildren(TimePickerComponent) timePickers: QueryList<TimePickerComponent>;
  @Input() operatingHours;
  @Output() modelChanged = new EventEmitter<{model:any,isValid:boolean}>();

  startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0);
  endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 45);
  days = {
    "SUNDAY": {value: '00:00', isValid: true},
    "MONDAY": {value: '00:00', isValid: true},
    "TUESDAY": {value: '00:00', isValid: true},
    "WEDNESDAY": {value: '00:00', isValid: true},
    "THURSDAY": {value: '00:00', isValid: true},
    "FRIDAY": {value: '00:00', isValid: true},
    "SATURDAY": {value: '00:00', isValid: true}
  }
  dayNames = Object.keys(this.days)
  constructor(private cdr: ChangeDetectorRef, private elRef: ElementRef) { }

  ngOnInit(): void {
  }

  onTimeChanged($event: any, day:string) {
    const picker = this.timePickers.find(tp => tp.name === day);
    if (picker) {
      this.validatePicker(picker, picker.selectedTimeString || '');
    }
  }

  validatePicker(timePicker: TimePickerComponent, value: string) {
    const isValid = this.isValidTime(value);
    if (isValid) {
      this.days[timePicker.name].value = value;
    }
    this.days[timePicker.name].isValid = isValid;

    // Directly toggle error-border class on the SOL input container
    const container = timePicker.elementRef.nativeElement.querySelector('.sol-input-container');
    if (container) {
      if (!isValid) {
        container.classList.add('error-border');
      } else {
        container.classList.remove('error-border');
      }
    }

    this.notifyModelChange();
    this.cdr.detectChanges();
  }

  isValidTime(value: string): boolean {
    if (!value || value.trim() === '') return false;
    if (!/^\d{2}:\d{2}$/.test(value)) return false;
    const [hours, minutes] = value.split(':').map(Number);
    if (hours > 24) return false;
    if (hours === 24 && minutes > 0) return false;
    if (minutes < 0 || minutes >= 60) return false;
    return true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.operatingHours){
      this.setOperatingHours();
    }
  }
  ngAfterViewInit() {
    // Prevent SOL's container click handler from refocusing input on bubbled clicks
    TextInputComponent.prototype.onInputContainerClick = function() {};

    this.timePickers.toArray().forEach((timePicker) => {
      // SOL generates options using DatePipe which uses 12-hour format for 'hh:mm'.
      // Replace with full 00:00–24:00 range in 15-min increments.
      timePicker.timeOptions = [];
      for (let h = 0; h <= 23; h++) {
        for (let m = 0; m < 60; m += 15) {
          timePicker.timeOptions.push(
            `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
          );
        }
      }
      timePicker.timeOptions.push("24:00");

      // Override to prevent SOL from resetting selectedTimeString or stealing focus
      (timePicker as any).validateAndUpdateTouched = () => {};
      (timePicker as any).onFocusOut = () => {};
      // Prevent SOL's focusInput from refocusing after dropdown close
      (timePicker as any).focusInput = () => {};

      // Override SOL's onChange to prevent auto-conversion of partial input.
      // Only accept exact NN:NN format; otherwise mark invalid.
      timePicker.onChange = (searchText: string) => {
        timePicker.selectedTimeString = searchText;
        this.validatePicker(timePicker, searchText);
      };

      // Override selectedTime to blur input after dropdown selection
      const origSelectedTime = timePicker.selectedTime.bind(timePicker);
      timePicker.selectedTime = (time: string) => {
        origSelectedTime(time);
        setTimeout(() => {
          const inputEl = timePicker.elementRef.nativeElement.querySelector('input') as HTMLInputElement;
          if (inputEl) {
            inputEl.blur();
          }
        }, 50);
      };

      // Listen on native input for deletions and focus management
      setTimeout(() => {
        const inputEl = timePicker.elementRef.nativeElement.querySelector('input') as HTMLInputElement;
        const container = timePicker.elementRef.nativeElement.querySelector('.sol-input-container');

        if (inputEl) {
          // Replace focus() to block SOL's programmatic refocus calls.
          // Only allow focus from trusted user interactions (click/tab).
          let allowFocus = false;
          const origFocusFn = inputEl.focus.bind(inputEl);
          inputEl.focus = function() {
            if (allowFocus) {
              origFocusFn();
            }
          };
          inputEl.addEventListener('mousedown', () => { allowFocus = true; });
          inputEl.addEventListener('mouseup', () => { setTimeout(() => { allowFocus = false; }); });
          // Allow keyboard tab focus
          inputEl.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Tab') { allowFocus = true; setTimeout(() => { allowFocus = false; }); }
          });

          inputEl.addEventListener('input', () => {
            const value = inputEl.value || '';
            timePicker.selectedTimeString = value;
            this.validatePicker(timePicker, value);
          });
          inputEl.addEventListener('focus', () => {
            container?.classList.add('esp-focused');
          });
          inputEl.addEventListener('blur', () => {
            container?.classList.remove('esp-focused');
          });
        }
      });
    });
  }

  setOperatingHours() {
    if(this.operatingHours){
      Object.keys(this.days).forEach((day)=>{
        let isMultiValue = this.operatingHours[day] ==="--:--";
        this.days[day].value = isMultiValue?this.operatingHours[day]:DateUtils.convertMinutesToTimeString(this.operatingHours[day]);
        this.days[day].isValid = true;
      });
      setTimeout(()=>{
        this.timePickers.toArray().forEach((timePicker)=>{
          timePicker.selectedTimeString = this.days[timePicker.name].value;
          const container = timePicker.elementRef.nativeElement.querySelector('.sol-input-container');
          container?.classList.remove('error-border');
          (timePicker as any).cdr.detectChanges();
        });
      },0)
    }
  }



  notifyModelChange() {
    let isValid = true;
    let model = {};
    Object.keys(this.days).forEach((day)=>{
      isValid = isValid && this.days[day].isValid;
      model[day]=this.days[day].value;
      if(model[day]!=="--:--" && isValid){
        model[day]=DateUtils.convertTimeStringToMinutes(model[day]);
      }
    });
    this.modelChanged.emit({model,isValid});
  }
}
